import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { createCustomerPortal } from "../api/billing";
import { useHostToast } from "../../host/useHostToast";

interface UseCreateCustomerPortal {
  pending: boolean;
  open: (input?: { isUpdatingPlan?: boolean }) => Promise<void>;
}

// The Stripe portal's return_url is built host-side as
// `${baseUrlPresenter}${pathname}`, so pathname must be a presenter route.
// This app runs in a cross-origin iframe, so its own `window.location.pathname`
// (e.g. "/team") is NOT a presenter route and 404s the "Return to AhaSlides"
// link — return the user to the presenter team page instead. See AHA-47024.
const PRESENTER_RETURN_PATH = "/apps/team/members";

export function useCreateCustomerPortal(): UseCreateCustomerPortal {
  const { t } = useTranslation();
  const toast = useHostToast();
  const [pending, setPending] = useState(false);

  const open = useCallback(
    async (input: { isUpdatingPlan?: boolean } = {}) => {
      setPending(true);
      try {
        const { url } = await createCustomerPortal({
          pathname: PRESENTER_RETURN_PATH,
          isUpdatingPlan: input.isUpdatingPlan,
        });
        if (url) window.open(url, "_blank", "noopener");
      } catch {
        toast.error(t("team.toast.genericError"));
      } finally {
        setPending(false);
      }
    },
    [t, toast],
  );

  return { open, pending };
}
